import { useState } from "react"
import CurrencyIcon from "./CurrencyIcon"
import AlertMeter from "./AlertMeter"
import EditAlertThreshold from "./EditAlertThreshold"
import { formatPrice } from "@/utils/format"
import { router } from "@inertiajs/react"
import type { Alert as AlertType } from "@/types/types"
import clsx from "clsx"
import { TrashIcon } from "@heroicons/react/16/solid"

type Props = {
  alert: Omit<AlertType, "gain_threshold" | "drop_threshold">
  className?: string
}

const Alert = ({ alert, className }: Props) => {
  const [isHovering, setIsHovering] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { id } = alert
  const deleteAlert = async () => {
    if (confirm("Are you sure you want to delete this alert?")) {
      setIsSubmitting(true)
      router.delete(`/alerts/${id}`, {
        onFinish: () => setIsSubmitting(false),
        preserveState: true,
        preserveScroll: true,
      })
    }
  }

  const saveChanges = async (change_threshold: number) => {
    setIsSubmitting(true)
    router.put(
      `/alerts/${id}`,
      {
        alert: { change_threshold },
      },
      {
        onFinish: () => {
          setIsSubmitting(false)
          setIsEditing(false)
        },
        preserveState: true,
        preserveScroll: true,
      }
    )
  }

  return (
    <div
      className={clsx(
        "relative rounded-lg bg-gray-800 select-none",
        {
          "cursor-pointer": !isEditing,
        },
        className
      )}
      onClick={() => setIsEditing(true)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="flex h-12 text-lg lg:h-16">
        <div className="flex min-w-[7.5rem] flex-1 items-center p-4 lg:w-1/4 lg:flex-none">
          <div className="flex-none">
            <CurrencyIcon src={alert.currency.logo_url} />
          </div>
          <div className="ml-4">{alert.currency.symbol}</div>
        </div>
        {isEditing ? (
          <div className="hidden lg:flex lg:flex-1">
            <EditAlertThreshold
              onSubmit={saveChanges}
              initialThreshold={alert.change_threshold}
              isSubmitting={isSubmitting}
            />
          </div>
        ) : (
          <div className="flex flex-1 items-center">
            <div className="hidden flex-1 lg:block">
              <AlertMeter
                threshold={alert.change_threshold}
                change={alert.change}
                highlightThreshold={isHovering}
              />
            </div>
            <div
              className="min-w-20 flex-1 p-4 text-right lg:w-1/3 lg:flex-none"
              title={
                alert.reference_price
                  ? `Reference price: ${formatPrice(alert.reference_price)}`
                  : undefined
              }
            >
              {formatPrice(alert.currency.price)}
            </div>
          </div>
        )}
      </div>
      <div className="flex h-12 w-full items-center px-4 pt-2 pb-4 lg:hidden">
        {isEditing ? (
          <EditAlertThreshold
            onSubmit={saveChanges}
            initialThreshold={alert.change_threshold}
            isSubmitting={isSubmitting}
          />
        ) : (
          <div className="flex-1">
            <AlertMeter
              threshold={alert.change_threshold}
              change={alert.change}
              highlightThreshold={isHovering}
            />
          </div>
        )}
      </div>
      {isEditing && (
        <div className="absolute top-0 right-0 mr-16 flex h-12 p-2 pl-4 lg:mr-0 lg:h-full lg:translate-x-full lg:transform lg:p-0">
          <button
            className="left-full flex flex-none items-center justify-center rounded bg-gray-700 px-3 text-gray-400 hover:text-red-400 lg:bg-transparent lg:px-4 lg:text-gray-700"
            disabled={isSubmitting}
            onClick={deleteAlert}
          >
            <span className="sr-only">Delete alert</span>
            <TrashIcon className="h-3 w-3 fill-current lg:h-4 lg:w-4" />
          </button>
        </div>
      )}
    </div>
  )
}

export default Alert
